import 'whatwg-fetch';
import _cloneDeep from 'lodash/cloneDeep';
import { NO_CONTENT } from '@localytics/redux-simple-api-middleware/lib/constants';
import * as Types from '../constants/ChainedApiActionTypes';
import * as ApiTypes from '../constants/ApiActionTypes';
import log from '../util/ErrorUtils';
import { defaultConfig, EXIT_TYPE, ENTRY_TYPE } from '../constants/ApiTypes';
import { ORCH_ENDPOINT_PATH } from '../config';

export const handleResponse = async response => {
  /*
    Different fetch implementations handle parsing JSON of a 204 response
    differently, so here we specifically catch the No Content/Empty response and
    return a resolved promise.
  */
  const { status, ok } = response;
  if (response.status === NO_CONTENT) {
    return { status };
  }
  const json = await response.json();

  if (!ok) {
    throw Error({ json, status });
  }
  return json;
};

const orchHeaders = {
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'content-type': 'application/json'
  },
  credentials: 'include'
};
/*
  orgId: number,
  name: string,
  appGroupId: number,
  apps: Array<{ app_uuid: string, olap_id: number }>,
  events: Array<{ name: string, olap_id: number }>
*/
export const createNewOrchAndVersion = (
  routerHistory,
  orgId,
  name,
  appGroupId,
  apps,
  events,
  eventMappingId
) => async dispatch => {
  dispatch({ type: Types.CREATE_NEW_ORCH_REQUEST });
  try {
    const prefixedPath = `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
      orgId
    }/orchestration`;

    const orchResponse = await fetch(prefixedPath, {
      method: 'POST',
      ...orchHeaders,
      body: JSON.stringify({
        name,
        ...defaultConfig
      })
    });
    const orchJson = await handleResponse(orchResponse);

    const orchVerResponse = await fetch(
      `${prefixedPath}/${orchJson.orchestration.id}/version`,
      {
        method: 'POST',
        ...orchHeaders,
        body: JSON.stringify({
          apps,
          nodes: {
            1: {
              node_type: ENTRY_TYPE,
              event_mapping_id: eventMappingId,
              events,
              next: 2
            },
            2: {
              node_type: EXIT_TYPE
            }
          },
          app_group_id: appGroupId
        })
      }
    );
    const orchVerJson = await handleResponse(orchVerResponse);
    const { orchestration_version: version } = orchVerJson;
    const { orchestration } = orchJson;
    dispatch({
      type: Types.CREATE_NEW_ORCH_SUCCESS,
      payload: {
        version,
        orchestration
      },
      // TODO: this is to feed the tree builder with the nodes in orchVerJSON. Need to reconsider
      // filling the response here.
      response: orchVerJson
    });

    routerHistory.push(
      `/show?orgId=${orgId}&orchId=${orchestration.id}&versionId=${version.id}`
    );
  } catch (e) {
    const action = {
      type: Types.CREATE_NEW_ORCH_FAILURE,
      payload: {
        message: e.json || e.title || e.message || 'Unexpected Error'
      }
    };
    log(new Error(action.type), { ...action });
    dispatch(action);
  }
};

export const duplicateOrchVersionAndCreatives = (
  routerHistory,
  orgId,
  orchVersion,
  creatives
) => async dispatch => {
  dispatch({ type: Types.CREATE_NEW_ORCH_VER_REQUEST });
  try {
    const prefixedPath = `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
      orgId
    }/orchestration/${orchVersion.orchestration_id}/version`;

    // Create new orch version without nodes or creatives
    const orchVerResponse = await fetch(prefixedPath, {
      method: 'POST',
      ...orchHeaders,
      body: JSON.stringify({
        nodes: {},
        apps: orchVersion.apps
      })
    });

    const orchVerJson = await handleResponse(orchVerResponse);
    const { orchestration_version: version } = orchVerJson;

    const creativePath = `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
      orgId
    }/orchestration/${version.orchestration_id}/version/${
      version.id
    }/creatives`;

    // Duplicate creative content from old version
    const creativesForCopy = [];
    Object.keys(orchVersion.nodes).forEach(key => {
      if (orchVersion.nodes[key].node_type === 'message') {
        orchVersion.nodes[key].creative_ids.forEach(originalCreativeId => {
          const creativeContent = creatives.find(
            creative => creative.id === originalCreativeId
          );

          creativesForCopy.push({
            nodeId: key,
            creative: {
              message_type: creativeContent.message_type,
              olap_id: creativeContent.olap_id,
              platform: creativeContent.platform,
              app_uuid: creativeContent.app_uuid,
              data: creativeContent.data
            }
          });
        });
      }
    });

    // Create new node structure, create new creatives, then plug new creative idsinto new node data
    const newNodes = _cloneDeep(orchVersion.nodes);
    Object.keys(newNodes).forEach(key => {
      if (newNodes[key].creative_ids != null) {
        delete newNodes[key].creative_ids;
        newNodes[key].creative_ids = [];
      }
    });
    Promise.all(
      creativesForCopy.map(item =>
        fetch(creativePath, {
          method: 'POST',
          ...orchHeaders,
          body: JSON.stringify(item.creative)
        })
      )
    )
      .then(responses => {
        Promise.all(responses.map(response => handleResponse(response)))
          .then(async responseJsons => {
            responseJsons.forEach((responseJson, index) => {
              newNodes[creativesForCopy[index].nodeId].creative_ids = newNodes[
                creativesForCopy[index].nodeId
              ].creative_ids.concat(responseJson.creative.id);
            });

            // Finally, update orchestration version with new node data with new creative ids
            const updatedOrchVerResponse = await fetch(
              `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
                orgId
              }/orchestration/${version.orchestration_id}/version/${
                version.id
              }`,
              {
                ...orchHeaders,
                method: 'PUT',
                body: JSON.stringify({
                  nodes: newNodes,
                  apps: version.apps,
                  app_group_id: orchVersion.app_group_id
                })
              }
            );

            const updatedOrchVerJson = await handleResponse(
              updatedOrchVerResponse
            );
            const {
              orchestration_version: updatedVersion
            } = updatedOrchVerJson;

            dispatch({
              type: Types.CREATE_NEW_ORCH_VER_SUCCESS,
              payload: {
                updatedVersion
              },
              // TODO: this is to feed the tree builder with the nodes in orchVerJSON. Need to reconsider
              // filling the response here.
              response: updatedOrchVerJson
            });

            dispatch({
              type: ApiTypes.ORCH_VERSION_SUCCESS,
              payload: {
                versionId: updatedVersion.id
              },
              response: updatedOrchVerJson
            });

            routerHistory.replace(
              `/show?orgId=${orgId}&orchId=${
                updatedVersion.orchestration_id
              }&versionId=${updatedVersion.id}`
            );
          })
          .catch(e => {
            const action = {
              type: Types.CREATE_NEW_ORCH_VER_FAILURE,
              payload: {
                message: e.json || e.title || e.message || 'Unexpected Error'
              }
            };
            log(new Error(action.type), { ...action });
            dispatch(action);
          });
      })
      .catch(e => {
        const action = {
          type: Types.CREATE_NEW_ORCH_VER_FAILURE,
          payload: {
            message: e.json || e.title || e.message || 'Unexpected Error'
          }
        };
        log(new Error(action.type), { ...action });
        dispatch(action);
      });
  } catch (e) {
    const action = {
      type: Types.CREATE_NEW_ORCH_VER_FAILURE,
      payload: {
        message: e.json || e.title || e.message || 'Unexpected Error'
      }
    };
    log(new Error(action.type), { ...action });
    dispatch(action);
  }
};



// WEBPACK FOOTER //
// ./src/actions/chainedApi.js